/**
 * The versions of a dataset: its history, newest first, each one openable,
 * and the notice shown when the items on screen are not the latest version.
 */

import { Upload } from "lucide-react";
import { Box, BoxHeader } from "@/components/ui/box";
import { Button, ButtonLink } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { EmptyState } from "@/components/ui/states";
import { formatDate } from "@/lib/format";
import { isLive, versionPeriod } from "./live/version-period";
import type { Dataset } from "./types";

export function NewVersionButton({
  datasetId,
  variant = "primary",
}: {
  datasetId: string;
  variant?: "primary" | "default";
}) {
  return (
    <ButtonLink variant={variant} to={`/datasets/${datasetId}/import`}>
      <Upload aria-hidden className="size-4" />
      New version
    </ButtonLink>
  );
}

export function VersionsPanel({
  dataset,
  viewing,
  onOpen,
}: {
  dataset: Dataset;
  viewing: number | null;
  onOpen: (version: number) => void;
}) {
  const latest = dataset.latest_version?.version;
  const live = isLive(dataset);
  return (
    <Box>
      <BoxHeader title="Version history">
        <span className="flex-1 text-xs text-muted">
          {live
            ? "A version never changes. Each experiment run pulls a new version from the source."
            : "A version never changes. Each CSV upload saves a new one, and experiments keep the version they ran on."}
        </span>
        {!live && <NewVersionButton datasetId={dataset.id} variant="default" />}
      </BoxHeader>
      <table className="tbl">
        <thead>
          <tr>
            <th>Version</th>
            <th className="num">Items</th>
            {live && <th>Period</th>}
            <th>Note</th>
            <th>Date</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {dataset.versions.map((v) => (
            <tr key={v.id}>
              <td>
                <span className="flex items-center gap-2">
                  <span className="font-mono">v{v.version}</span>
                  {v.version === latest && <Pill>Latest</Pill>}
                </span>
              </td>
              <td className="num">{v.item_count}</td>
              {live && <td>{versionPeriod(v) || <span className="text-muted">—</span>}</td>}
              <td>{v.note || <span className="text-muted">—</span>}</td>
              <td className="text-muted">{formatDate(v.created_at)}</td>
              <td className="text-right">
                {v.version === viewing ? (
                  <span className="text-xs text-muted">Viewing</span>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => onOpen(v.version)}>
                    View items
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dataset.versions.length === 0 && <EmptyState title="No versions yet" />}
    </Box>
  );
}

/** Shown above the items of an older version, so nobody mistakes it for the current one. */
export function OldVersionNotice({
  viewing,
  latest,
  onLatest,
}: {
  viewing: number;
  latest: number;
  onLatest: () => void;
}) {
  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-lg border border-line bg-subtle px-4 py-2.5 text-[13px]"
    >
      <span className="flex-1">
        You are viewing <span className="font-mono">v{viewing}</span>, an older version. The latest is{" "}
        <span className="font-mono">v{latest}</span>.
      </span>
      <Button size="sm" onClick={onLatest}>
        View latest
      </Button>
    </div>
  );
}
